import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Instagram, Facebook, MessageCircle, Lock } from "lucide-react";
import { SITE, NAV, SLOGAN } from "@/config/site";
import { RoadDash } from "./bits";

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-joell-ink" data-testid="site-footer">
      <div className="container-x grid gap-12 py-16 md:grid-cols-2 md:py-20 lg:grid-cols-4">
        <div>
          <img src="/logo.png" alt="JOELL" className="h-16 w-auto" />
          <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
            Asphalt Service • Maintenance
          </p>
          <p className="mt-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-joell-yellow">
            “{SLOGAN}”
          </p>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
            Proudly serving homes, businesses and properties across Bermuda.
          </p>
          <div className="mt-6 flex gap-3">
            <a href={SITE.instagram} target="_blank" rel="noreferrer" aria-label="JOELL on Instagram" className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-joell-yellow hover:text-joell-yellow" data-testid="footer-instagram-link">
              <Instagram size={17} />
            </a>
            <a href={SITE.facebook} target="_blank" rel="noreferrer" aria-label="JOELL on Facebook" className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-joell-yellow hover:text-joell-yellow" data-testid="footer-facebook-link">
              <Facebook size={17} />
            </a>
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" aria-label="JOELL on WhatsApp" className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-joell-yellow hover:text-joell-yellow" data-testid="footer-whatsapp-link">
              <MessageCircle size={17} />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">Quick Links</h3>
          <ul className="mt-5 space-y-3">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-sm text-slate-300 transition-colors hover:text-joell-yellow">{item.label}</Link>
              </li>
            ))}
            <li>
              <Link to="/careers" className="text-sm text-slate-300 transition-colors hover:text-joell-yellow">Careers</Link>
            </li>
            <li>
              <Link to="/news" className="text-sm text-slate-300 transition-colors hover:text-joell-yellow">News</Link>
            </li>
            <li>
              <Link to="/request-quote" className="text-sm font-semibold text-white transition-colors hover:text-joell-yellow">Request a Quote</Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">Services</h3>
          <ul className="mt-5 space-y-3 text-sm text-slate-300">
            <li><Link to="/maintenance" className="transition-colors hover:text-joell-yellow">Asphalt Paving &amp; Repairs</Link></li>
            <li><Link to="/maintenance" className="transition-colors hover:text-joell-yellow">Driveways &amp; Parking Areas</Link></li>
            <li><Link to="/maintenance" className="transition-colors hover:text-joell-yellow">Property Maintenance</Link></li>
            <li><Link to="/maintenance" className="transition-colors hover:text-joell-yellow">Repairs &amp; Upkeep</Link></li>
            <li><Link to="/our-work" className="transition-colors hover:text-joell-yellow">Completed Projects</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">Contact</h3>
          <ul className="mt-5 space-y-4 text-sm text-slate-300">
            <li>
              <a href={SITE.phoneHref} className="flex items-center gap-3 transition-colors hover:text-joell-yellow" data-testid="footer-phone-link">
                <Phone size={15} className="shrink-0 text-joell-yellow" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={SITE.phone2Href} className="flex items-center gap-3 transition-colors hover:text-joell-yellow" data-testid="footer-phone2-link">
                <Phone size={15} className="shrink-0 text-joell-yellow" /> {SITE.phone2}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 break-all transition-colors hover:text-joell-yellow" data-testid="footer-email-link">
                <Mail size={15} className="shrink-0 text-joell-yellow" /> {SITE.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Globe size={15} className="shrink-0 text-joell-yellow" /> {SITE.website}
            </li>
          </ul>
          <RoadDash className="mt-6 w-40" green />
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 sm:flex-row">
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 sm:text-left">
            © 2026 JOELL Asphalt Service &amp; Maintenance Ltd. All Rights Reserved.
          </p>
          <Link to="/admin" className="inline-flex items-center gap-2 border border-white/25 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300 transition-colors hover:border-joell-yellow hover:text-joell-yellow" data-testid="footer-admin-link">
            <Lock size={12} /> Owner Dashboard
          </Link>
        </div>
      </div>
    </footer>
  );
}

export function MobileContactBar() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 border-t border-white/10 bg-joell-coal/95 backdrop-blur-lg sm:hidden" data-testid="mobile-contact-bar">
      <a href={SITE.phoneHref} className="flex h-14 items-center justify-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-white active:bg-white/10" data-testid="mobile-bottom-call-button">
        <Phone size={15} className="text-joell-yellow" /> Call
      </a>
      <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="flex h-14 items-center justify-center gap-2 border-x border-white/10 font-mono text-[11px] font-bold uppercase tracking-widest text-white active:bg-white/10" data-testid="mobile-bottom-whatsapp-button">
        <MessageCircle size={15} className="text-joell-yellow" /> WhatsApp
      </a>
      <Link to="/request-quote" className="flex h-14 items-center justify-center gap-2 bg-joell-yellow font-mono text-[11px] font-bold uppercase tracking-widest text-joell-black active:bg-white" data-testid="mobile-bottom-quote-button">
        Get Quote
      </Link>
    </div>
  );
}
